import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

/**
 * Lịch chạy quảng cáo luôn tính theo giờ Việt Nam (server so theo
 * Asia/Ho_Chi_Minh — xem hp-media-server-staging/src/image-ads/helpers/ad-schedule.ts),
 * nên ở đây quy đổi tường minh sang +07:00 thay vì dùng giờ của máy admin.
 */
const VN_OFFSET_MINUTES = 7 * 60;

export const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm:ss';
export const TIME_FORMAT = 'HH:mm:ss';

export type AdRunStatus = 'RUNNING' | 'PAUSED' | 'SCHEDULED' | 'ENDED' | 'OUT_OF_HOURS';

export interface ImageAdItem {
  id: number;
  image: string;
  url?: string | null;
  isDisplay: boolean;
  startAt?: string | null;
  endAt?: string | null;
  dailyStartTime?: string | null;
  dailyEndTime?: string | null;
  runStatus?: AdRunStatus;
  createdAt: string;
}

// Màu thẻ dùng bảng màu có sẵn của antd ('green', 'blue', 'gold') như các trang khác, không
// dùng 'success'/'processing'/'warning': các màu đó sinh từ token theme (colorSuccess
// #0F7A46 đậm) nên nền thẻ ra xám xanh đục, chữ khó đọc.
export const RUN_STATUS: Record<AdRunStatus, { label: string; color: string; hint: string }> = {
  RUNNING: {
    label: 'Đang chạy',
    color: 'green',
    hint: 'Trong lịch chạy — app hiện ảnh này khi công tắc "Hiện quảng cáo" đang bật.',
  },
  SCHEDULED: { label: 'Chờ bắt đầu', color: 'blue', hint: 'Chưa tới thời gian bắt đầu.' },
  OUT_OF_HOURS: {
    label: 'Ngoài khung giờ',
    color: 'gold',
    hint: 'Trong thời gian chạy nhưng lúc này ngoài khung giờ mỗi ngày.',
  },
  ENDED: { label: 'Đã kết thúc', color: 'default', hint: 'Đã qua thời gian kết thúc.' },
  PAUSED: { label: 'Tạm dừng', color: 'default', hint: 'Đã tắt riêng ảnh này.' },
};

/** Mốc lưu trên server (ISO) → giờ đồng hồ VN, để hiển thị/điền vào DatePicker. */
export const toVnWallClock = (iso?: string | null): Dayjs | null =>
  iso ? dayjs(dayjs.utc(iso).utcOffset(VN_OFFSET_MINUTES).format('YYYY-MM-DDTHH:mm:ss')) : null;

/** Giờ đồng hồ chọn trên DatePicker (hiểu là giờ VN) → ISO có múi giờ gửi server. */
export const fromVnWallClock = (value?: Dayjs | null): string | null =>
  value ? `${value.format('YYYY-MM-DDTHH:mm:ss')}+07:00` : null;

/** 'HH:mm:ss' (MySQL TIME) ↔ Dayjs cho TimePicker. */
export const parseTimeOfDay = (value?: string | null): Dayjs | null =>
  value ? dayjs(value.slice(0, 8), TIME_FORMAT) : null;

export const formatVn = (iso?: string | null) => toVnWallClock(iso)?.format(DATE_TIME_FORMAT) ?? '';

export const isOvernight = (start?: string | null, end?: string | null) =>
  !!start && !!end && start.slice(0, 8) > end.slice(0, 8);

export const describeDateRange = (startAt?: string | null, endAt?: string | null) => {
  if (startAt && endAt) return `${formatVn(startAt)} → ${formatVn(endAt)}`;
  if (startAt) return `Từ ${formatVn(startAt)}, không hẹn ngày tắt`;
  if (endAt) return `Chạy ngay, tắt lúc ${formatVn(endAt)}`;
  return 'Chạy liên tục, không hẹn ngày tắt';
};

export const describeDailyWindow = (start?: string | null, end?: string | null) => {
  if (!start || !end) return 'Cả ngày';
  const s = start.slice(0, 8);
  const e = end.slice(0, 8);
  return `Mỗi ngày ${s} – ${e}${isOvernight(s, e) ? ' (qua đêm)' : ''}`;
};

/** Giờ VN hiện tại dưới dạng giờ đồng hồ — mốc cho các nút chọn nhanh. */
export const nowInVn = () =>
  dayjs(dayjs().utcOffset(VN_OFFSET_MINUTES).format('YYYY-MM-DDTHH:mm:ss'));
